import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStore } from 'effector-react';
import { get } from 'lodash';
import moment from 'moment';
import cn from 'classnames';
import {
  Avatar,
  Popconfirm,
  Spin,
  Tag,
  Button,
  Tooltip,
  Divider,
  Dropdown,
  Menu,
  message,
  Popover,
  Table,
} from 'antd';
import { LockOutlined, LoadingOutlined, DownOutlined, EditOutlined } from '@ant-design/icons';
import { getPath } from '@helpers/navigation';
import routes from '@config/routes';
import user, {
  getItem,
  revokePlus,
  getNicknameHistory,
  getPlusHistory,
  getModerationHistory,
} from '@stores/user';
import users, { unblockUser, blockUser } from '@stores/users';
import auth from '@stores/auth';
import UserBlockMenu from '@components/UserBlockMenu';
import Image from '@components/Image';
import useTranslate from '@hooks/useTranslate';
import IUser, { IPlus, IModerationHistoryRow } from '@interfaces/IUser';

import UserClaimsTrigger from '@components/UserClaimsTrigger/UserClaimsTrigger';
import UserChatClaimsTrigger from '@components/UserChatClaimsTrigger/UserChatClaimsTrigger';
import UserMessagesTrigger from '@components/UserMessagesTrigger/UserMessagesTrigger';
import UserSuspiciousChatsTrigger from '@components/UserSuspiciousChatsTrigger/UserSuspiciousChatsTrigger';
import AccountStateTag from '@components/AccountStateTag';
import UserUdidsModal from '@components/UserUdidsModal';

import componentContent from './UserInfo.content.json';
import UserTooltip from './UserInfo.content';
import componentUsersContent from '@components/UsersTable/UsersTable.content.json';
import css from './UserInfo.module.less';
import ModerationHistoryTable from './ModerationHistoryTable';
import AccountChangeHistory from './AccountChangeHistory';
import { openModal } from '@stores/modals';
import type { UserBlockReason } from '@constants/userBlockOptions';

const PLUS_TYPE = {
  admin: 'INTERNAL_TRIAL',
  trial: 'TRIAL',
  paid: 'REGULAR',
};

const PLUS_TYPE_REVERSE = {
  INTERNAL_TRIAL: 'Admin',
  TRIAL: 'Trial',
  REGULAR: 'Paid',
};

interface UserInfoProps {}

const UserInfo: FunctionComponent<UserInfoProps> = () => {
  const {
    item,
    preloaders,
    plus,
    nicknameHistory,
    plusHistoryRaw,
    paginationPlusHistory,
    setPaginationPlusHistory,
    moderationHistoryRaw,
    paginationModerationHistory,
    setPaginationModerationHistory,
  } = useStore(user);

  const content = useTranslate(componentContent);

  const plusHistory = (plusHistoryRaw?.content as IPlus[]) ?? [];
  const totalPlusHistory = plusHistoryRaw?.totalElements ?? 0;

  const moderationHistory = (moderationHistoryRaw?.content as IModerationHistoryRow[]) ?? [];
  const totalModerationHistory = moderationHistoryRaw?.totalElements ?? 0;

  const { preloaders: usersPreloaders } = useStore(users);
  const usersContent = useTranslate(componentUsersContent);
  const { user: authUser } = useStore(auth);

  const unblock = useCallback(async () => {
    if (item) {
      await unblockUser({ userId: item.id, preloaderId: item.id, user: item });

      await getModerationHistory({
        userId: item.id,
        paginationModerationHistory,
      });
    }
  }, [item, paginationModerationHistory]);

  useEffect(() => {
    if (item?.id) {
      getNicknameHistory({ userId: item.id });
      getPlusHistory({ userId: item.id, paginationPlusHistory });
      getModerationHistory({
        userId: item.id,
        paginationModerationHistory,
      });
    }
  }, [item?.id]);

  const onBlock = useCallback(
    async (user: IUser, daysToUnblock?: number, daysToClean?: number, reason?: UserBlockReason) => {
      if (user) {
        await blockUser({
          userId: user.id,
          preloaderId: user.id,
          user,
          daysToUnblock,
          daysToClean,
          reason,
        });

        await getModerationHistory({
          userId: user.id,
          paginationModerationHistory,
        });
      }
    },
    [paginationModerationHistory],
  );

  const addPlus = useCallback(() => {
    openModal({ name: 'plus' });
  }, []);

  const continuePlus = useCallback(() => {
    openModal({
      name: 'continuePlus',
      data: {
        plusDefaultStartDate: plus?.endDate,
      },
    });
  }, [plus]);

  const removePlus = useCallback(
    async (id: IUser['id']) => {
      await revokePlus({ accountId: id });
      await getItem({ userId: id });
    },
    [item],
  );

  const isMe = authUser?.id === item?.id;
  const isAdmin = authUser?.role === 'ROLE_ADMIN';
  const isModerator = authUser?.role === 'ROLE_MODERATOR';
  const isBlocked = !item?.enable;
  const isBlocking = usersPreloaders.blockUser === item?.id;
  const isUnblocking = usersPreloaders.unblockUser === item?.id;
  const isBlockProcessing = isBlocking || isUnblocking;
  const isDeleted = moment(item?.deactivatedUntil ?? null).isValid();

  const nicknameHistoryComponent = useMemo(() => {
    if (nicknameHistory?.length > 0) {
      const nicknameHistoryMas = nicknameHistory.map((item) => ({
        nickname: item.newValue,
        date: moment(item.createdAt).format('DD.MM.YYYY'),
      }));

      return (
        <Dropdown
          overlay={
            <Menu>
              {nicknameHistoryMas.map((item) => {
                return (
                  <Menu.Item key={item.nickname + String(item.date)}>
                    {item.nickname}
                    <Divider
                      type='vertical'
                      className={css.metaItemDivider}
                      style={{ marginLeft: '5px', marginRight: '5px' }}
                    />
                    {item.date}
                  </Menu.Item>
                );
              })}
            </Menu>
          }
          trigger={['click']}
        >
          <Button
            style={{
              marginRight: '10px',
              height: '20px',
              maxWidth: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DownOutlined />
          </Button>
        </Dropdown>
      );
    }
    return null;
  }, [nicknameHistory, item?.id]);

  const [visiblePlusHistory, setVisiblePlusHistory] = useState(false);
  const [isFirstLoadPlusHistory, setIsFirstLoadPlusHistory] = useState(true);
  const [isUdidsModalOpened, setIsUdidsModalOpened] = useState(false);

  useEffect(() => {
    if (plusHistoryRaw !== null) setIsFirstLoadPlusHistory(false);
  }, [plusHistoryRaw]);

  const plusHistoryTable = useMemo(() => {
    const dataSource = plusHistory.map((item, index) => ({
      key: index,
      date: `${moment(item.startDate).format('DD.MM.YYYY')} - ${moment(item.endDate).format(
        'DD.MM.YYYY',
      )}`,
      type: PLUS_TYPE_REVERSE[item.type as keyof typeof PLUS_TYPE_REVERSE] || item.type,
    }));

    const columns = [
      {
        title: 'Даты',
        dataIndex: 'date',
        key: 'date',
      },
      {
        title: 'Тип',
        dataIndex: 'type',
        key: 'type',
      },
    ];

    if (item?.id)
      return (
        <Table
          columns={columns}
          loading={!!preloaders.getPlusHistory}
          dataSource={dataSource}
          showHeader={false}
          pagination={{
            current: paginationPlusHistory.current,
            pageSize: paginationPlusHistory.pageSize,
            total: totalPlusHistory,
            onChange: (page, pageSize) => {
              setPaginationPlusHistory({ current: page, pageSize });
              getPlusHistory({
                userId: item.id,
                paginationPlusHistory: { current: page, pageSize },
              });
            },
            position: ['topRight'],
            size: 'small',
          }}
        />
      );
    return (
      <Spin
        size='small'
        style={{
          marginLeft: 8,
        }}
      />
    );
  }, [
    plusHistory,
    preloaders.getPlusHistory,
    paginationPlusHistory.current,
    paginationPlusHistory.pageSize,
    totalPlusHistory,
    item?.id,
    setPaginationPlusHistory,
  ]);

  const plusHistoryComponent = useMemo(() => {
    if ((!plusHistory || preloaders.getPlusHistory) && isFirstLoadPlusHistory) {
      return (
        <Spin
          size='small'
          style={{ marginLeft: 8 }}
        />
      );
    }
    if (plusHistory.length === 0) return null;

    if (item?.id) {
      return (
        <Popover
          placement='left'
          trigger='click'
          visible={visiblePlusHistory}
          onVisibleChange={setVisiblePlusHistory}
          content={<div onClick={(e) => e.stopPropagation()}>{plusHistoryTable}</div>}
        >
          <Button
            icon={<DownOutlined />}
            style={{
              marginLeft: 8,
              height: 20,
              width: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Popover>
      );
    }
    return null;
  }, [visiblePlusHistory, plusHistory, preloaders.getPlusHistory, plusHistoryTable, item?.id]);

  const nicknameContent = (nickname?: string) => {
    if (nickname)
      return (
        <span className={css.info}>
          {`${content.nickname}:`}
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            {nicknameHistoryComponent}
            <div
              className={css.clickable}
              onClick={() => {
                navigator.clipboard.writeText(nickname);
                message.success(`${content.copy}: ${nickname}`);
              }}
            >
              {nickname}
            </div>
          </div>
        </span>
      );
    return null;
  };

  const moderationHistoryComponent = (
    <ModerationHistoryTable
      itemId={item?.id}
      moderationHistory={moderationHistory}
      moderationHistoryRaw={moderationHistoryRaw}
      totalModerationHistory={totalModerationHistory}
      paginationModerationHistory={paginationModerationHistory}
      setPaginationModerationHistory={setPaginationModerationHistory}
      getModerationHistoryLoading={!!preloaders.getModerationHistory}
      authUser={authUser ?? undefined}
      onFetchModerationHistory={getModerationHistory}
    />
  );

  const mainInfoContent = (contentValue: string, textValue?: string) => {
    if (textValue)
      return (
        <span className={css.info}>
          {`${contentValue}:`}
          <div
            className={css.clickable}
            onClick={() => {
              navigator.clipboard.writeText(textValue);
              message.success(`${content.copy}: ${textValue}`);
            }}
          >
            {textValue}
          </div>
        </span>
      );
    return null;
  };

  const registrationIpContent = (registrationIp?: string) => {
    return (
      <span className={css.info}>
        {`${content.registrationIp}:`}
        {registrationIp ? (
          <div
            className={css.clickable}
            onClick={() => {
              window.open(
                getPath(routes.userList.name, undefined, { registrationIp }),
                '_blank',
                'noopener,noreferrer',
              );
            }}
          >
            {registrationIp}
          </div>
        ) : (
          <div className={css.noClickable}>—</div>
        )}
      </span>
    );
  };

  const birthDateContent = (birthDate?: string) => {
    const date = moment(birthDate);
    const formattedDate = birthDate && date.isValid() ? date.format('DD.MM.YYYY') : undefined;

    return (
      <span className={css.info}>
        {`${content.birthDate}:`}
        {formattedDate ? (
          <div
            className={css.clickable}
            onClick={() => {
              navigator.clipboard.writeText(formattedDate);
              message.success(`${content.copy}: ${formattedDate}`);
            }}
          >
            {formattedDate}
          </div>
        ) : (
          <div className={css.noClickable}>—</div>
        )}
      </span>
    );
  };

  const moderationHistoryContent = (node?: React.ReactNode) => {
    return (
      <span
        className={css.info}
        style={{
          marginLeft: 8,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        {node}
      </span>
    );
  };

  const statusPlus = useMemo(() => {
    if (item?.isPlus) {
      const activeDateFrom = moment(plus?.startDate).format('DD.MM.YYYY');
      const activeDateUntil = moment(plus?.endDate).format('DD.MM.YYYY');
      return (
        <div>
          <span className={css.plus}>
            {`${content.gameramPlus} (${
              plus?.type
                ? PLUS_TYPE_REVERSE[plus.type as keyof typeof PLUS_TYPE_REVERSE]
                : 'unknown'
            }): ${content.plusActive} ${content.from}`}
            <div
              className={css.clickable}
              onClick={() => {
                navigator.clipboard.writeText(activeDateFrom);
                message.success(`${content.copy}: ${activeDateFrom}`);
              }}
            >
              {activeDateFrom}
            </div>
            {`${content.until}`}
            <div
              className={css.clickable}
              onClick={() => {
                navigator.clipboard.writeText(activeDateUntil);
                message.success(`${content.copy}: ${activeDateUntil}`);
              }}
            >
              {activeDateUntil}
            </div>
            {plusHistoryComponent}
          </span>
          <div></div>
          <Button
            onClick={() => removePlus(item?.id ?? '')}
            type='primary'
          >
            {content.gameramPlusInactive}
          </Button>
          {plus?.type === PLUS_TYPE.admin && (
            <Button
              type='primary'
              onClick={continuePlus}
              style={{ marginLeft: '10px' }}
            >
              {content.gameramPlusContinue}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div>
        <span className={css.plus}>
          {`${content.gameramPlus}:`}
          <div className={css.noClickable}>{content.plusInactive}</div>
          {plusHistoryComponent}
        </span>
        <Button
          type='primary'
          onClick={addPlus}
        >
          {content.gameramPlusActive}
        </Button>
      </div>
    );
  }, [item, plus, content]);

  const mainInfo = useMemo(() => {
    const discordUsername = item?.linkedServices?.find(
      (service) => service.provider === 'DISCORD',
    )?.username;

    return (
      <div className={css.name_nickname}>
        {nicknameContent(item?.nickname)}
        {birthDateContent(item?.birthDate)}
        {mainInfoContent(content.name, item?.name)}
        {mainInfoContent(
          content.registrationDate,
          moment(item?.registrationDate).format('DD.MM.YYYY'),
        )}
        {registrationIpContent(item?.registrationIp)}
        {discordUsername ? (
          mainInfoContent('Discord', discordUsername)
        ) : (
          <span className={css.info}>
            Discord:
            <div className={css.noClickable}>—</div>
          </span>
        )}
        {isAdmin ? mainInfoContent(content.email, item?.email) : null}
      </div>
    );
  }, [item, nicknameHistory, plusHistory, isAdmin]);

  const renderLabelWithTooltip = (getParam: string) => (
    <>{UserTooltip(content[getParam], getParam)}</>
  );

  const renderLabelWithContent = (getParam: string) => <>{content[getParam]}</>;

  const metaItemBlock = (
    getParam: string | string[],
    isDivider: boolean,
    renderLabel?: React.ReactNode,
  ) => {
    const itemsMas = get(item, getParam, 0);

    const value = () => {
      if (typeof itemsMas === 'string') return itemsMas;
      if (itemsMas?.length >= 0) return itemsMas?.length;
      return itemsMas;
    };

    return (
      <div className={css.metaItem}>
        <div className={css.metaItemLabel}>{renderLabel}</div>
        <div className={css.metaItemNumber}>{value()}</div>
        {isDivider && (
          <Divider
            type='vertical'
            className={css.metaItemDivider}
          />
        )}
      </div>
    );
  };

  const UserPersonal = useMemo(() => {
    return (
      <div className={css.metaLine}>
        {get(item, ['games', 'length'], 0) > 0 ? (
          <Link to={getPath(routes.userDetailGames.name, { userId: item?.id })}>
            {metaItemBlock('gamesCount', true, renderLabelWithContent('games'))}
          </Link>
        ) : (
          <>{metaItemBlock(['games', 'length'], true, renderLabelWithContent('games'))}</>
        )}
        {metaItemBlock('subscribersCount', true, renderLabelWithContent('followers'))}
        {metaItemBlock('followingsCount', true, renderLabelWithContent('followings'))}
        {metaItemBlock('receivedLikesCount', true, renderLabelWithContent('receivedLikesCount'))}
        {get(item, 'postsCount', 0) > 0 ? (
          <Link to={getPath(routes.userDetail.name, { userId: item?.id })}>
            {metaItemBlock('postsCount', false, renderLabelWithContent('postsCount'))}
          </Link>
        ) : (
          <>{metaItemBlock('postsCount', false, renderLabelWithContent('postsCount'))}</>
        )}
      </div>
    );
  }, [item]);

  const UserActivity = useMemo(() => {
    return (
      <div className={css.metaLine}>
        {metaItemBlock(
          'receivedCommentsCount',
          true,
          renderLabelWithTooltip('receivedCommentsCount'),
        )}
        {metaItemBlock('sentLikesCount', true, renderLabelWithTooltip('sentLikesCount'))}
        {get(item, 'sentCommentsCount', 0) > 0 ? (
          <Link
            target='_blank'
            to={getPath(routes.userDetailComments.name, { userId: item?.id })}
          >
            {metaItemBlock('sentCommentsCount', true, renderLabelWithTooltip('sentCommentsCount'))}
          </Link>
        ) : (
          <>
            {metaItemBlock('sentCommentsCount', true, renderLabelWithTooltip('sentCommentsCount'))}
          </>
        )}
        {metaItemBlock('chatTotalCount', true, renderLabelWithTooltip('chatTotalCount'))}
        {metaItemBlock(
          'chatCreatedByUserCount',
          true,
          renderLabelWithTooltip('chatCreatedByUserCount'),
        )}
        {metaItemBlock('tribes', true, renderLabelWithTooltip('tribes'))}
        {metaItemBlock('statuses', true, renderLabelWithTooltip('statuses'))}
        <button
          className={css.counterLink}
          type='button'
          onClick={() => setIsUdidsModalOpened(true)}
        >
          {metaItemBlock('devicesCount', false, renderLabelWithContent('udids'))}
        </button>
      </div>
    );
  }, [item]);

  const UserClaims = useMemo(() => {
    const claimsMas = [
      'accountClaimsCount',
      'postsClaimsCount',
      'commentsClaimsCount',
      'statusesClaimsCount',
    ];

    return (
      <div className={css.actionItem}>
        <Dropdown
          overlay={
            <Menu>
              {claimsMas.map((claim) => {
                return (
                  <Menu.Item key={claim}>
                    {metaItemBlock(claim, false, renderLabelWithContent(claim))}
                  </Menu.Item>
                );
              })}
            </Menu>
          }
          trigger={['click']}
        >
          <Button
            style={{
              marginRight: '10px',
              marginBottom: '8px',
              height: '20px',
              maxWidth: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <DownOutlined />
          </Button>
        </Dropdown>

        <UserClaimsTrigger user={item} />
      </div>
    );
  }, [item]);

  return (
    <Spin spinning={!!preloaders.getItem || !!preloaders.getNicknameHistory}>
      <div className={css.root}>
        <div className={css.avatarWrapper}>
          <Avatar
            icon={item?.photo || item?.image ? <Image url={item?.image?.url} /> : null}
            size={120}
          >
            {item?.nickname?.[0]?.toUpperCase()}
          </Avatar>
          {item && (isAdmin || isModerator) && !isMe ? (
            <Tooltip title={usersContent.editUser}>
              <Button
                className={css.editUserButton}
                icon={<EditOutlined />}
                shape='circle'
                aria-label={usersContent.editUser}
                onClick={() => openModal({ name: 'user', data: item })}
              />
            </Tooltip>
          ) : null}
        </div>

        <div className={css.meta}>
          <div className={css.mainColumn}>
            <div className={css.moderationRow}>
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                {item && !item?.enable ? (
                  <Popconfirm
                    title={content.unblockConfirmText}
                    onConfirm={unblock}
                  >
                    <Tag
                      color='error'
                      className={css.tagNoOverflow}
                    >
                      {preloaders.unblockUser === item?.id ? (
                        <LoadingOutlined />
                      ) : (
                        <span className={css.blocked}>{content.blocked}</span>
                      )}
                    </Tag>
                  </Popconfirm>
                ) : null}

                {item && !isMe ? (
                  <Tooltip title={isBlocked ? usersContent.unblock : usersContent.block}>
                    {!isBlocked && (
                      <UserBlockMenu
                        user={item}
                        onBlock={onBlock}
                      >
                        <Button
                          disabled={isBlockProcessing}
                          loading={isBlockProcessing}
                          icon={<LockOutlined />}
                          shape='circle'
                        />
                      </UserBlockMenu>
                    )}
                  </Tooltip>
                ) : null}

                {item && isDeleted && (
                  <Tag color='error'>
                    <span className={css.blocked}>{content.deleted.toUpperCase()}</span>
                  </Tag>
                )}

                {item && (
                  <AccountStateTag
                    accountState={item.accountState}
                    enable={item.enable}
                    banEndedAt={item.banEndedAt}
                  />
                )}
              </div>
              {moderationHistoryContent(moderationHistoryComponent)}
            </div>
            {item && (
              <AccountChangeHistory
                key={item.id}
                userId={item.id}
              />
            )}
            <div className={css.name}>
              <div className={css.name_nickname}>{mainInfo}</div>
            </div>

            <div className={css.plusRow}>{statusPlus}</div>
          </div>

          <div className={css.countersColumn}>
            {UserPersonal}
            {UserActivity}

            <div className={css.actionsRow}>
              {UserClaims}
              <div className={css.actionItem}>
                <UserChatClaimsTrigger user={item} />
              </div>
              <div className={css.actionItem}>
                <UserMessagesTrigger userId={item?.id} />
              </div>
              <div className={css.actionItem}>
                <UserSuspiciousChatsTrigger user={item} />
              </div>
            </div>
          </div>
        </div>

        <div className={css.text}>{item?.about}</div>

        {item?.link ? (
          <a
            target='_blank'
            rel='noopener noreferrer'
            href={item?.link}
            className={cn(css.link, 'clickable')}
          >
            {item?.link}
          </a>
        ) : null}

        <UserUdidsModal
          accountId={item?.id}
          devicesCount={item?.devicesCount}
          visible={isUdidsModalOpened}
          onClose={() => setIsUdidsModalOpened(false)}
        />
      </div>
    </Spin>
  );
};

export default UserInfo;
